import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, shareReplay } from 'rxjs';
import { TableColumn } from 'src/app/models/table-column.model';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { CourseTemplateService } from 'src/app/services/course-template.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ToastrService } from 'ngx-toastr';
import { DatePipe } from '@angular/common';
import { CourseTemplateDateModel } from 'src/app/models/course/course-template-date.model';
import { CourseTemplateManageSummaryModel } from 'src/app/models/course/course-template-manage-summary.model';
import { ComplianceRenewalFrequencyModel } from 'src/app/models/compliance/compliance-renewal-frequency.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { getTrainingTypeLabel } from 'src/app/utils/training-type.utils';

@Component({
  selector: 'app-manage-elearning-overview',
  templateUrl: './manage-elearning-overview.component.html',
  styleUrl: './manage-elearning-overview.component.scss',
  standalone: false
})
export class ManageElearningOverviewComponent implements OnInit {
  allCourses$!: Observable<CourseTemplateManageSummaryModel[]>;
  allCourses: CourseTemplateManageSummaryModel[] = [];
  selectedElerningIds: string[] = [];
  showMergeModal = false;
  showHideModal = false;
  selectedElerningsToMergeOrHide: CourseTemplateManageSummaryModel[] = [];

  columns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-150',
      cellValue: (data: CourseTemplateManageSummaryModel): string => {
        return (
          data.name +
          (data.isDeployed
            ? ''
            : ` - ${this.translateService.instant(
                'MANAGE-ELEARNING.OVERVIEW.DRAFT'
              )}`
          ) +
          (data.trainingType == TrainingTypeModel.Compliance && data.date && data.renewalFrequency !== ComplianceRenewalFrequencyModel.Never
            ? ` (${this.displayCourseDate(data.date)})`
            : ''
          )
        );
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAINING-TYPE'),
      dataField: 'trainingType',
      cellValue: (data: CourseTemplateManageSummaryModel): string => {
        return getTrainingTypeLabel(data.trainingType, this.translateService)
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.STATUS'),
      dataField: 'isDeployed',
      cellTemplate: 'statusTemplate',
      cellValue: (data: CourseTemplateManageSummaryModel): string => {
        return data.isDeployed
          ? this.translateService.instant('COMMON.PUBLICATION-STATUS.PUBLISHED')
          : this.translateService.instant('COMMON.PUBLICATION-STATUS.DRAFT');
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAINING-GROUPS'),
      dataField: 'trainingGroups',
      cssClass: 'width-250',
      headerFilter: (data: any) => {
        data.dataSource.postProcess = (results: any) => {
          const trainingGroupNames = results.reduce(
            (a: any, b: any) => a.concat(b.value),
            []
          );
          const parsedTrainingGroupNames: string[] = [];
          trainingGroupNames.forEach((group: string) => {
            if (!group || !group.includes(', ')) {
              parsedTrainingGroupNames.push(group);
            } else if (group.includes(', ')) {
              const groups = group.split(', ');
              parsedTrainingGroupNames.push(...groups);
            }
          });
          const uniqueTrainingGroupNames = [
            ...new Set(parsedTrainingGroupNames),
          ];

          results.splice(0, results.length);
          uniqueTrainingGroupNames.forEach((tgName) => {
            if (tgName) {
              results.push({
                text: tgName,
                value: tgName,
              });
            } else {
              results.push({
                text: '(Blanks)',
                value: null,
              });
            }
          });
          return results;
        };
      },
      filterExpression: (value: any) => {
        function getTrainingGroupsFilterString(rowData: CourseTemplateManageSummaryModel) {
          const groups = rowData.trainingGroups.map((tg) => tg.name);
          return !!groups.join(',') ? groups.join(',') + ',' : 'empty';
        }

        return [
          [
            getTrainingGroupsFilterString,
            'contains',
            value ? value + ',' : 'empty',
          ],
        ];
      },
      cellValue: (data: CourseTemplateManageSummaryModel): string => {
        return data.trainingGroups.map((tg: any) => tg.name).join(', ');
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (course: CourseTemplateManageSummaryModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();
            this.navigateToCourseNewTab(course.id);
          },
        },
      ],
    },
  ];

  readonly stateKey = 'course-templates-overview';

  constructor(
    private courseService: CourseTemplateService,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private router: Router,
    private toastrService: ToastrService,
    private pageStateService: PageStateService
  ) {}

  ngOnInit(): void {
     if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreCourseTemplatesOverview
    )) {
      this.pageStateService.clear(this.stateKey);
    }

    this.loadElerningsList();
  }

  loadElerningsList() {
    this.allCourses$ = this.loadingService.load(
      this.courseService.getAllCourseTemplatesForManagement()
    ).pipe(shareReplay(1));

    this.allCourses$.subscribe((allCourses) => {
      this.allCourses = allCourses;
    });
  }

  navigateToCourseNewTab(id: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['manage-elearning', id])
    );
    window.open(url, '_blank');
  }

  navigateToCourse(id?: string) {
    this.router.navigate(['manage-elearning', id || 'new']);
  }

  navigateToNewCourse() {
    this.router.navigate(['manage-elearning', 'edit', 'new']);
  }

  onElerningsSelected(ids: string[]) : void {
    this.selectedElerningIds = ids;
  }

  showMergeModalHandle() : void {
    this.selectedElerningsToMergeOrHide = this.allCourses.filter((elerning) =>
      this.selectedElerningIds.includes(elerning.id)
    );
    this.showMergeModal = true;
  }

  showArchiveModalHandle() : void {
    this.selectedElerningsToMergeOrHide = this.allCourses.filter((elerning) =>
      this.selectedElerningIds.includes(elerning.id)
    );
    this.showHideModal = true;
  }

  mergeElernings(mergeTargetId: string) {
    const toMergeElernings = this.selectedElerningsToMergeOrHide.filter(
      (elerning) => elerning.id !== mergeTargetId
    );

    const request = this.courseService.mergeCourses({
      targetCourseId: mergeTargetId,
      courseIds: toMergeElernings.map(x => x.id)
    });

    this.loadingService.load(request).subscribe(
      () => {
        this.toastrService.success(
          this.translateService.instant('MANAGE-ELEARNING.OVERVIEW.MERGE-SUCCESS')
        );
        this.loadElerningsList();
        this.selectedElerningIds = [];
        this.selectedElerningsToMergeOrHide = [];
        this.showHideModal = false;
      },
      (error) => {
        this.toastrService.error(
          this.translateService.instant('MANAGE-ELEARNING.OVERVIEW.MERGE-ERROR')
        );
      }
    );
  }

  archiveElernings() : void {
    const request = this.courseService.archiveCourses(this.selectedElerningIds);

    this.loadingService.load(request).subscribe(
      () => {
        this.toastrService.success(
          this.translateService.instant('MANAGE-ELEARNING.OVERVIEW.ARCHIVE-SUCCESS')
        );
        this.loadElerningsList();
        this.selectedElerningIds = [];
        this.selectedElerningsToMergeOrHide = [];
        this.showHideModal = false;
      },
      (error) => {
        this.toastrService.error(
          this.translateService.instant('MANAGE-ELEARNING.OVERVIEW.ARCHIVE-ERROR')
        );
      }
    );
  }

  displayCourseDate(date: CourseTemplateDateModel) {
    const datePipe = new DatePipe(this.translateService.currentLang);
    
    const startDateStr = datePipe.transform(date.start, 'dd MMMM yyyy');
    const endDateStr = datePipe.transform(date.end, 'dd MMMM yyyy');
    return startDateStr + ' - ' + endDateStr;
  }
}
